import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import SocialLinks from './SocialLinks';
import './ResponsiveFooter.css';
import { useCanCollectPayments } from '../hooks/useCanCollectPayments';

const ResponsiveFooter = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const isHome = location.pathname === '/';
  const { canCollect } = useCanCollectPayments();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const discoverLinks = [
    { to: '/category/hotels', label: t(language, 'footer.hotels') },
    { to: '/category/restaurants', label: t(language, 'footer.restaurants') },
    { to: '/category/loisirs', label: t(language, 'footer.leisure') },
    { to: '/category/administrations', label: t(language, 'footer.admin') },
    { to: '/category/hopitaux', label: t(language, 'footer.hospitals') },
    { to: '/category/pharmacies', label: t(language, 'footer.pharmacies') },
  ];

  const serviceLinks = [
    { to: '/allo', label: t(language, 'footer.alloTaxi') },
    { to: '/location-voiture', label: t(language, 'footer.carRental') },
    { to: '/ecosystem', label: t(language, 'footer.ecosystem') },
    { to: '/events', label: t(language, 'footer.events') },
    { to: '/news', label: t(language, 'footer.news') },
    { to: '/vivre-en-guinee', label: t(language, 'footer.vivre') },
    { to: '/contact', label: t(language, 'footer.contact') },
  ];

  const businessLinks = [
    { to: '/register-business', label: t(language, 'footer.registerBusiness') },
    { to: '/devenir-agent', label: t(language, 'footer.becomeAgent') },
    { to: '/register-taxi', label: t(language, 'footer.registerTaxi') },
    { to: '/register-rental', label: t(language, 'footer.registerRental') },
    { to: '/register-activity', label: t(language, 'footer.registerActivity') },
    ...(canCollect
      ? [{ to: '/ecosystem/encaisser', label: t(language, 'footer.collect') }]
      : []),
  ];

  const renderLinkGroup = (title, links, openByDefault = false) => {
    if (isMobile) {
      return (
        <details className="site-footer__accordion" open={openByDefault}>
          <summary className="site-footer__heading site-footer__heading--summary">
            {title}
          </summary>
          <ul className="site-footer__links site-footer__links--grid">
            {links.map((item) => (
              <li key={item.to}>
                <Link to={item.to}>{item.label}</Link>
              </li>
            ))}
          </ul>
        </details>
      );
    }

    return (
      <div>
        <h6 className="site-footer__heading">{title}</h6>
        <ul className="site-footer__links">
          {links.map((item) => (
            <li key={item.to}>
              <Link to={item.to}>{item.label}</Link>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <footer
      className={[
        'site-footer',
        isMobile ? 'site-footer--mobile' : '',
        isHome ? 'site-footer--home' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="site-footer__glow" aria-hidden="true" />

      <div className="site-footer__shell">
        {!isHome && (
          <div className="site-footer__cta">
            <div className="site-footer__cta-copy">
              <p className="site-footer__eyebrow">{t(language, 'footer.forBusiness')}</p>
              <h2>{t(language, 'footer.ctaTitle')}</h2>
              {!isMobile && <p>{t(language, 'footer.ctaDescription')}</p>}
            </div>
            <div className="site-footer__cta-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.65rem', alignItems: 'center' }}>
              <Link to="/register-business" className="site-footer__cta-btn">
                {t(language, 'footer.ctaButton')}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <Link
                to="/devenir-agent"
                className="site-footer__cta-btn"
                style={{
                  background: 'transparent',
                  color: 'inherit',
                  border: '1px solid currentColor',
                  opacity: 0.95
                }}
              >
                {t(language, 'footer.ctaAgent')}
              </Link>
            </div>
          </div>
        )}

        <div className={`site-footer__grid${isMobile ? ' site-footer__grid--mobile' : ''}`}>
          <div className="site-footer__brand">
            <Link to="/" className="site-footer__logo">
              <img src="/logo.png" alt="" className="site-footer__logo-img" width={40} height={40} />
              <span className="site-footer__name">Conakry en Poche</span>
            </Link>
            <p className="site-footer__tagline">
              {isMobile
                ? t(language, 'footer.taglineShort')
                : t(language, 'footer.tagline')}
            </p>
            <ul className={`site-footer__contact${isMobile ? ' site-footer__contact--compact' : ''}`}>
              <li>
                <a href="mailto:admin@conakryenpoche.com">admin@conakryenpoche.com</a>
              </li>
              <li>
                <a
                  href="https://wa.me/224663078268"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp +224 663 07 82 68
                </a>
              </li>
              {!isMobile && <li>{t(language, 'footer.location')}</li>}
            </ul>
            <SocialLinks variant="footer" />
          </div>

          {renderLinkGroup(t(language, 'footer.discover'), discoverLinks, true)}
          {renderLinkGroup(t(language, 'footer.services'), serviceLinks, false)}
          {renderLinkGroup(t(language, 'footer.forBusiness'), businessLinks, false)}
        </div>

        <div className="site-footer__bottom">
          <p>
            &copy; 2025 Conakry en Poche — {t(language, 'footer.rights')}
          </p>
          <div className="site-footer__legal">
            <Link to="/contact">{t(language, 'footer.contact')}</Link>
            <a href="/politique-confidentialite.html">{t(language, 'footer.privacy')}</a>
            <a href="/conditions-utilisation.html">{t(language, 'footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ResponsiveFooter;
